import { useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, ShieldCheck, ArrowRight, Send } from "lucide-react";
import { ASSETS } from "@/lib/constants";
import { track, EVENTS } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OPTS = {
  experience: [
    ["lt1", "Less than 1 year"],
    ["1_3", "1–3 years"],
    ["3_5", "3–5 years"],
    ["5_10", "5–10 years"],
    ["10_plus", "10+ years"],
  ],
  loans_closed: [
    ["0_5", "0–5"],
    ["6_12", "6–12"],
    ["13_24", "13–24"],
    ["25_49", "25–49"],
    ["50_plus", "50+"],
  ],
  business_mix: [
    "Real estate agent relationships",
    "Past clients / sphere / referrals",
    "Builder or professional relationships",
    "Online / purchased leads",
    "Company-provided leads",
    "Self-generated prospecting",
    "Balanced mix of several sources",
    "Still building my business",
  ],
  agent_relationships: [
    ["lt5", "Fewer than 5"],
    ["5_14", "5–14"],
    ["15_29", "15–29"],
    ["30_49", "30–49"],
    ["50_plus", "50+"],
  ],
  crm_discipline: [
    ["daily", "Daily / highly disciplined"],
    ["several_week", "Several times each week"],
    ["weekly", "Weekly"],
    ["occasional", "Occasionally"],
    ["rarely", "Rarely"],
    ["none", "I do not currently have an effective CRM system"],
  ],
  bd_consistency: [
    ["daily", "Daily"],
    ["several_week", "Several times each week"],
    ["weekly", "Weekly"],
    ["few_month", "A few times per month"],
    ["occasional", "Occasionally"],
    ["need_help", "I need help creating a consistent system"],
  ],
  bdm_potential: [
    ["0", "0"],
    ["1_2", "1–2"],
    ["3_5", "3–5"],
    ["6_10", "6–10"],
    ["11_plus", "11+"],
  ],
  growth_goals: [
    "Build significantly more mortgage production",
    "Develop more Realtor and referral relationships",
    "Build a team/network of active BDMs",
    "Create a more consistent prospecting and follow-up system",
    "Build or expand my mortgage team",
    "Transition to NEXA and access a broader growth platform",
    "A combination of the above",
  ],
  coachability: [
    ["very_high", "Very high"],
    ["high", "High"],
    ["moderate", "Moderate"],
    ["low", "Low"],
  ],
  leadership: [
    ["build_org", "I want to build and lead a significant MLO/BDM organization."],
    ["recruit_mlos", "I want to recruit and mentor additional MLOs."],
    ["develop_bdms", "I want to develop multiple productive BDM relationships."],
    ["own_production", "I primarily want to grow my own personal production."],
    ["unsure", "I am not sure yet but would like to explore it."],
  ],
};

const AGREEMENTS = [
  { key: "a1", title: "Active Growth Commitment", body: "I understand that the Great Rate Check MLO Growth Program is an active business-development platform and that successful participation requires consistent effort, follow-up, and execution." },
  { key: "a2", title: "NEXA Onboarding & Licensing", body: "I understand that participation in Great Rate Check does not by itself constitute employment, sponsorship, licensing, or approval by NEXA and that I must separately satisfy all applicable NEXA onboarding, employment, licensing, and compliance requirements." },
  { key: "a3", title: "Training & Coaching", body: "I agree to complete required Great Rate Check onboarding and participate in applicable training, coaching, and growth meetings." },
  { key: "a4", title: "Approved Marketing & Compliance", body: "I agree to use approved Great Rate Check and NEXA messaging, marketing materials, disclosures, processes, and compliance procedures when representing either organization." },
  { key: "a5", title: "CRM & Follow-Up Discipline", body: "I agree to maintain an organized system for tracking appropriate business-development activity, relationships, opportunities, and follow-up." },
  { key: "a6", title: "BDM Development", body: "I agree to make a reasonable and consistent effort to identify, introduce, support, and develop qualified BDM participants within my market and professional relationships." },
  { key: "a7", title: "Lead & Opportunity Follow-Up", body: "I agree to provide professional, timely follow-up on consumer opportunities and introductions generated through the Great Rate Check program and to follow applicable program lead-management standards." },
  { key: "a8", title: "Professional Communication", body: "I agree to communicate professionally and responsively with Great Rate Check, NEXA, BDMs, referral partners, consumers, and other participating professionals." },
  { key: "a9", title: "Activity & Accountability", body: "I agree to participate in reasonable activity tracking, performance reviews, meetings, and reporting designed to improve results and maintain program standards." },
  { key: "a10", title: "Performance-Based Participation", body: "I understand that continued access to Great Rate Check resources, marketing support, leads, BDM opportunities, and other program benefits may depend on my activity, responsiveness, professionalism, compliance, and overall participation." },
];

const Section = ({ n, title, subtitle, children }) => (
  <section data-testid={`mlo-section-${n}`} className="bg-white rounded-3xl border border-[#EDE9DC] shadow-sm p-6 sm:p-8 mb-6">
    <div className="mb-6">
      <p className="text-[10px] uppercase tracking-widest text-[#1F8F8B]">Section {n}</p>
      <h2 className="mt-1 font-heading text-2xl sm:text-3xl text-[#0F2A47]">{title}</h2>
      {subtitle && <p className="mt-2 text-sm text-[#59564C] leading-relaxed">{subtitle}</p>}
    </div>
    <div className="space-y-5">{children}</div>
  </section>
);

const Field = ({ label, hint, children, required }) => (
  <label className="block">
    <span className="block text-sm font-medium text-[#0F2A47]">
      {label} {required && <span className="text-[#B04A2E]">*</span>}
    </span>
    {hint ? <span className="block text-xs text-[#7A7668] mt-0.5">{hint}</span> : null}
    <div className="mt-1.5">{children}</div>
  </label>
);

const TextInput = (props) => (
  <input
    {...props}
    className="w-full rounded-xl border border-[#E6E2D6] focus:border-[#1F8F8B] focus:outline-none focus:ring-2 focus:ring-[#1F8F8B]/20 p-3 text-sm text-[#0F2A47] bg-white"
  />
);

const TextArea = (props) => (
  <textarea
    {...props}
    className="w-full rounded-xl border border-[#E6E2D6] focus:border-[#1F8F8B] focus:outline-none focus:ring-2 focus:ring-[#1F8F8B]/20 p-3 text-sm text-[#0F2A47] bg-white"
  />
);

const Radio = ({ opts, value, onChange, testid }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={testid}>
    {opts.map(([val, label]) => (
      <label key={val} className={`flex items-center gap-2 rounded-xl border p-3 cursor-pointer transition ${
        value === val ? "border-[#1F8F8B] bg-[#1F8F8B]/5" : "border-[#E6E2D6] hover:border-[#1F8F8B]/60"
      }`}>
        <input type="radio" checked={value === val} onChange={() => onChange(val)} className="accent-[#1F8F8B]" />
        <span className="text-sm text-[#0F2A47]">{label}</span>
      </label>
    ))}
  </div>
);

const Checkboxes = ({ opts, values, onChange, testid }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={testid}>
    {opts.map((opt) => (
      <label key={opt} className={`flex items-center gap-2 rounded-xl border p-3 cursor-pointer transition ${
        values.includes(opt) ? "border-[#1F8F8B] bg-[#1F8F8B]/5" : "border-[#E6E2D6] hover:border-[#1F8F8B]/60"
      }`}>
        <input
          type="checkbox"
          checked={values.includes(opt)}
          onChange={() => onChange(values.includes(opt) ? values.filter((v) => v !== opt) : [...values, opt])}
          className="accent-[#1F8F8B]"
        />
        <span className="text-sm text-[#0F2A47]">{opt}</span>
      </label>
    ))}
  </div>
);

const initial = {
  full_name: "", email: "", mobile_phone: "", city_state: "", nmls_number: "",
  current_company: "", states_licensed: "",
  q1_experience: "", q1_loans_closed: "",
  q2_business_mix: [],
  q3_agent_relationships: "", q4_crm_discipline: "", q5_bd_consistency: "",
  q6_bdm_potential: "",
  q7_growth_goals: [],
  q8_coachability: "", q9_leadership: "", q10_why: "",
  agreements: [],
  applicant_name: "", signature: "", signed_date: new Date().toISOString().slice(0, 10),
};

export default function MLOApply() {
  const [f, setF] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState("");

  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const canSubmit = useMemo(() => {
    return (
      f.full_name.trim() && f.email.trim() && f.mobile_phone.trim() && f.nmls_number.trim() &&
      f.q1_experience && f.q1_loans_closed && f.q3_agent_relationships &&
      f.q4_crm_discipline && f.q5_bd_consistency && f.q6_bdm_potential &&
      f.q8_coachability && f.q9_leadership && f.q10_why.trim().length >= 20 &&
      f.agreements.length === AGREEMENTS.length &&
      f.applicant_name.trim() && f.signature.trim()
    );
  }, [f]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErr("");
    try {
      await axios.post(`${API}/mlo-applications`, f);
      track(EVENTS.LEAD_SUBMIT, { form: "mlo_growth_application" });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Submission failed. Please check required fields and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-6 py-16" data-testid="mlo-apply-success">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="max-w-lg w-full rounded-3xl bg-white border border-[#EDE9DC] p-10 text-center shadow-sm">
          <CheckCircle2 className="w-12 h-12 text-[#1F8F8B] mx-auto" />
          <h1 className="mt-4 font-heading text-3xl text-[#0F2A47]">Application received.</h1>
          <p className="mt-3 text-sm text-[#59564C] leading-relaxed">
            Thank you for applying to the Great Rate Check MLO Growth Program. We'll review your application and follow up with next steps within a few business days.
          </p>
          <a href="/" className="mt-6 inline-flex items-center gap-2 text-[#1F8F8B] font-medium hover:underline">
            Back to home <ArrowRight className="w-4 h-4" />
          </a>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="mlo-apply-page">
      {/* Hero */}
      <header
        className="text-white"
        style={{
          background: "linear-gradient(135deg, #08192F 0%, #0F2A47 45%, #17385e 100%)",
        }}
      >
        <div className="max-w-4xl mx-auto px-6 py-14 sm:py-20">
          <a href="/" className="inline-flex bg-white rounded-2xl px-3 py-1.5 shadow">
            <img src={ASSETS.logoHorizontal} alt="Great Rate Check" className="h-9 sm:h-11 w-auto" />
          </a>
          <p className="mt-8 text-[11px] font-bold uppercase tracking-[0.22em] text-[#A6D24A]">
            Great Rate Check MLO Growth Program Application
          </p>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl leading-[1.05] text-balance">
            Apply to the MLO Growth Program.{" "}
            <span className="italic text-[#A6D24A]">Then let's have a conversation.</span>
          </h1>
          <p className="mt-4 text-white/85 text-lg leading-relaxed">
            A few questions about how you work today — then a straight answer on whether this is a fit.
          </p>
          <div className="mt-8 space-y-4 text-white/80 text-sm leading-relaxed max-w-2xl">
            <p>
              Great Rate Check is building a growth platform for licensed Mortgage Loan Originators who want more than a place to originate loans.
            </p>
            <p>
              Our model is designed to help MLOs build relationships with real estate professionals, activate Business Development Marketers (BDMs), generate more mortgage conversations, and create a repeatable business-development system around their mortgage practice.
            </p>
            <p>
              Qualified MLOs may have the opportunity to join NEXA and participate in the Great Rate Check MLO Growth Program.
            </p>
            <p>
              We are looking for mortgage professionals who are relationship-driven, growth-oriented, coachable, organized, compliant, and ready to actively build their business.
            </p>
            <p className="text-white/60 italic">
              This application is for participation in the Great Rate Check MLO Growth Program. Employment, licensing, sponsorship, compensation, and approval with NEXA are subject to NEXA's separate requirements and onboarding process.
            </p>
          </div>
        </div>
      </header>

      <form onSubmit={submit} className="max-w-4xl mx-auto px-6 py-10">
        {/* Contact */}
        <Section n="1" title="Contact Information">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Full Name" required>
              <TextInput value={f.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="mlo-full-name" required />
            </Field>
            <Field label="Email Address" required>
              <TextInput type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="mlo-email" required />
            </Field>
            <Field label="Mobile Phone" required>
              <TextInput type="tel" value={f.mobile_phone} onChange={(e) => set("mobile_phone", e.target.value)} data-testid="mlo-phone" required />
            </Field>
            <Field label="City / State">
              <TextInput value={f.city_state} onChange={(e) => set("city_state", e.target.value)} data-testid="mlo-city-state" />
            </Field>
            <Field label="NMLS Number" required>
              <TextInput value={f.nmls_number} onChange={(e) => set("nmls_number", e.target.value)} data-testid="mlo-nmls" required />
            </Field>
            <Field label="Current Company / Mortgage Employer">
              <TextInput value={f.current_company} onChange={(e) => set("current_company", e.target.value)} data-testid="mlo-company" />
            </Field>
            <div className="sm:col-span-2">
              <Field label="States Currently Licensed In" hint="e.g., WA, OR, CA">
                <TextInput value={f.states_licensed} onChange={(e) => set("states_licensed", e.target.value)} data-testid="mlo-states" />
              </Field>
            </div>
          </div>
        </Section>

        {/* Q1–10 */}
        <Section n="2" title="MLO Qualification — 10 Questions">
          <Field label="1. Mortgage Experience — How long originating?" required>
            <Radio opts={OPTS.experience} value={f.q1_experience} onChange={(v) => set("q1_experience", v)} testid="mlo-q1-experience" />
          </Field>
          <Field label="Closed loans in the last 12 months" required>
            <Radio opts={OPTS.loans_closed} value={f.q1_loans_closed} onChange={(v) => set("q1_loans_closed", v)} testid="mlo-q1-loans" />
          </Field>
          <Field label="2. Current Business Mix — where does most of your business come from? (choose all that apply)">
            <Checkboxes opts={OPTS.business_mix} values={f.q2_business_mix} onChange={(v) => set("q2_business_mix", v)} testid="mlo-q2" />
          </Field>
          <Field label="3. Real Estate Agent Relationships — how many meaningful professional relationships?" required>
            <Radio opts={OPTS.agent_relationships} value={f.q3_agent_relationships} onChange={(v) => set("q3_agent_relationships", v)} testid="mlo-q3" />
          </Field>
          <Field label="4. CRM & Database Discipline" required>
            <Radio opts={OPTS.crm_discipline} value={f.q4_crm_discipline} onChange={(v) => set("q4_crm_discipline", v)} testid="mlo-q4" />
          </Field>
          <Field label="5. Business Development Consistency" required>
            <Radio opts={OPTS.bd_consistency} value={f.q5_bd_consistency} onChange={(v) => set("q5_bd_consistency", v)} testid="mlo-q5" />
          </Field>
          <Field label="6. BDM / Agent Growth Potential — how many could you realistically introduce in first 90 days?" required>
            <Radio opts={OPTS.bdm_potential} value={f.q6_bdm_potential} onChange={(v) => set("q6_bdm_potential", v)} testid="mlo-q6" />
          </Field>
          <Field label="7. Growth Goals — what do you want from the program? (choose all that apply)">
            <Checkboxes opts={OPTS.growth_goals} values={f.q7_growth_goals} onChange={(v) => set("q7_growth_goals", v)} testid="mlo-q7" />
          </Field>
          <Field label="8. Coachability & System Adoption" required>
            <Radio opts={OPTS.coachability} value={f.q8_coachability} onChange={(v) => set("q8_coachability", v)} testid="mlo-q8" />
          </Field>
          <Field label="9. Leadership & Recruiting Potential" required>
            <Radio opts={OPTS.leadership} value={f.q9_leadership} onChange={(v) => set("q9_leadership", v)} testid="mlo-q9" />
          </Field>
          <Field label="10. Why Great Rate Check + NEXA?" hint="At least a few sentences." required>
            <TextArea rows={5} value={f.q10_why} onChange={(e) => set("q10_why", e.target.value)} data-testid="mlo-q10" required />
          </Field>
        </Section>

        {/* Agreements */}
        <Section n="3" title="MLO Participation Agreement" subtitle="Please review and agree to the following participation standards.">
          <div className="space-y-3">
            {AGREEMENTS.map((a, i) => {
              const checked = f.agreements.includes(a.key);
              return (
                <label key={a.key} className={`flex gap-3 rounded-2xl border p-4 cursor-pointer transition ${
                  checked ? "border-[#1F8F8B] bg-[#1F8F8B]/5" : "border-[#E6E2D6] hover:border-[#1F8F8B]/60"
                }`}>
                  <input
                    type="checkbox"
                    className="mt-1 accent-[#1F8F8B] w-4 h-4"
                    checked={checked}
                    onChange={() => set("agreements", checked ? f.agreements.filter((k) => k !== a.key) : [...f.agreements, a.key])}
                    data-testid={`mlo-agreement-${a.key}`}
                  />
                  <div>
                    <p className="text-sm font-semibold text-[#0F2A47]">{i + 1}. {a.title}</p>
                    <p className="text-xs text-[#59564C] mt-1 leading-relaxed">{a.body}</p>
                    <p className={`mt-2 text-[10px] uppercase tracking-widest ${checked ? "text-[#1F8F8B]" : "text-[#7A7668]"}`}>
                      ☐ I Agree
                    </p>
                  </div>
                </label>
              );
            })}
          </div>
        </Section>

        {/* Certification */}
        <Section n="4" title="Certification" subtitle="By submitting this application, I certify that the information provided is accurate to the best of my knowledge. Great Rate Check will evaluate applicants based on overall fit rather than any single production threshold.">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Applicant Name" required hint="Your full legal name">
              <TextInput value={f.applicant_name} onChange={(e) => set("applicant_name", e.target.value)} data-testid="mlo-applicant-name" required />
            </Field>
            <Field label="Electronic Signature" required hint="Type your full legal name">
              <TextInput value={f.signature} onChange={(e) => set("signature", e.target.value)} data-testid="mlo-signature" required />
            </Field>
            <Field label="Date" hint="Today's date">
              <TextInput type="date" value={f.signed_date} onChange={(e) => set("signed_date", e.target.value)} />
            </Field>
          </div>
        </Section>

        {err && (
          <div className="rounded-2xl bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm mb-4" data-testid="mlo-error">
            {err}
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pb-16">
          <button
            type="submit"
            disabled={!canSubmit || submitting}
            data-testid="mlo-submit"
            className="inline-flex items-center gap-2 rounded-2xl bg-[#0F2A47] hover:bg-[#173a5f] text-white px-6 py-3 shadow disabled:opacity-60"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Apply to Join the MLO Growth Program
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        <div className="pb-10 text-center text-[11px] text-[#7A7668] flex items-center justify-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5" /> Great Rate Check · Your information is handled confidentially.
        </div>
      </form>
    </div>
  );
}
